import { AIConversationGenerationStatus } from '../../../../constants/aiAgents'
import {
  EMPTY_LIST,
  EMPTY_OBJECT,
  EMPTY_STRING,
} from '../../../../constants/common'
import { isValueNumberType } from '../../../../utils/common'
import {
  GRETA_AI_CHAT_SETTLED_GENERATION_GRACE_MS,
  GretaAIChatSource,
} from '../../GretaAIChat.constants'
import {
  getGretaAIChatChatById,
  getIsGretaAIChatChatRunning,
} from '../../GretaAIChat.utils'

function getIsGretaAIChatStaleRunning({ chat, requestedAt, settledAtById }) {
  return (
    getIsGretaAIChatChatRunning(chat) &&
    getIsGretaAIChatSettledRecently({ id: chat.id, requestedAt, settledAtById })
  )
}

export function getIsGretaAIChatSettledRecently({
  id,
  requestedAt,
  settledAtById,
}) {
  return (
    settledAtById.has(id) &&
    requestedAt - settledAtById.get(id) <
      GRETA_AI_CHAT_SETTLED_GENERATION_GRACE_MS
  )
}

export function mapGretaAIChatChat(chat) {
  return {
    generationStatus: chat.generation_status ?? null,
    id: chat.id,
    source: chat.source ?? GretaAIChatSource.Crm,
    title: chat.title ?? EMPTY_STRING,
    updatedAt: chat.updated ? new Date(chat.updated).getTime() : 0,
  }
}

export function mapGretaAIChatChats(response) {
  return (response?.data?.results ?? EMPTY_LIST).map(mapGretaAIChatChat)
}

export function getHasNextGretaAIChatChatsPage(response) {
  const { page, pages } = response?.data ?? EMPTY_OBJECT

  return isValueNumberType(page) && isValueNumberType(pages) && page < pages
}

export function getGretaAIChatChatsWithoutId(chats, id) {
  return chats.filter(chat => chat.id !== id)
}

export function getHasGretaAIChatChatId(chats, id) {
  return chats.some(chat => chat.id === id)
}

// A restored chat beyond the loaded pages comes from its own GET; the list copy wins once paged in.
export function getGretaAIChatActiveChat({
  activeChatId,
  chats,
  restoredChat,
}) {
  return (
    getGretaAIChatChatById(chats, activeChatId) ??
    (restoredChat?.id === activeChatId ? restoredChat : null)
  )
}

export function getGretaAIChatRestoredChatWithoutId(restoredChat, id) {
  return restoredChat?.id === id ? null : restoredChat
}

export function getGretaAIChatRestoredChatRenamed({ id, restoredChat, title }) {
  return restoredChat?.id === id ? { ...restoredChat, title } : restoredChat
}

// Roll back only the chat the failed request touched, so a concurrent restore is kept.
export function getGretaAIChatRestoredChatRolledBack({
  id,
  previousRestoredChat,
  restoredChat,
}) {
  return previousRestoredChat?.id === id ? previousRestoredChat : restoredChat
}

export function mergeGretaAIChatChats(primaryChats, secondaryChats) {
  const knownIds = new Set(primaryChats.map(chat => chat.id))

  return [
    ...primaryChats,
    ...secondaryChats.filter(chat => !knownIds.has(chat.id)),
  ]
}

export function getHasGretaAIChatStaleRunning({
  chats,
  requestedAt,
  settledAtById,
}) {
  return chats.some(chat =>
    getIsGretaAIChatStaleRunning({ chat, requestedAt, settledAtById })
  )
}

// A list read just after a turn settled can still say `running` — the replica lags.
export function getGretaAIChatChatsWithoutStaleRunning({
  chats,
  requestedAt,
  settledAtById,
}) {
  return chats.map(chat =>
    getIsGretaAIChatStaleRunning({ chat, requestedAt, settledAtById })
      ? { ...chat, generationStatus: AIConversationGenerationStatus.Idle }
      : chat
  )
}
